import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { api, mediaUrl, videoEmbed } from "../lib/api";
import { useSite } from "../context/SiteContext";
import { useSeo } from "../lib/useSeo";
import { Reveal } from "../components/Reveal";
import { ProjectCard } from "../components/ProjectCard";
import { CollabCTA } from "../components/CollabCTA";

const VideoBlock = ({ project }) => {
  const v = videoEmbed(project.video_url);
  if (!v) return null;
  const portrait = project.orientation === "portrait";
  return (
    <Reveal className={`mx-auto ${portrait ? "max-w-md" : "max-w-5xl"}`}>
      <p className="eyebrow mb-6 text-wine">The Film</p>
      <div className={`relative overflow-hidden bg-charcoal ${portrait ? "aspect-[9/16]" : "aspect-video"}`} data-testid="project-video">
        {v.type === "iframe" ? (
          <iframe src={v.src} title={`${project.title} film`} loading="lazy" allow="autoplay; fullscreen; picture-in-picture" allowFullScreen className="absolute inset-0 h-full w-full" />
        ) : (
          <video src={v.src} poster={mediaUrl(project.cover_image)} controls playsInline preload="metadata" className="absolute inset-0 h-full w-full object-cover" />
        )}
      </div>
    </Reveal>
  );
};

const Meta = ({ label, value }) =>
  value ? (
    <div className="border-t border-line py-4">
      <dt className="text-[0.65rem] uppercase tracking-[0.25em] text-muted-ink">{label}</dt>
      <dd className="mt-1 text-sm text-charcoal">{value}</dd>
    </div>
  ) : null;

export default function ProjectDetail() {
  const { slug } = useParams();
  const { projects } = useSite();
  const [project, setProject] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    setProject(null);
    setMissing(false);
    api.get(`/projects/${slug}`).then((r) => setProject(r.data)).catch(() => setMissing(true));
  }, [slug]);

  useSeo(project ? `${project.title} — ${project.category}` : "Project", project?.description?.slice(0, 155));

  if (missing)
    return (
      <div className="mx-auto max-w-[1400px] px-6 pb-40 pt-48 md:px-10">
        <h1 className="font-serif text-5xl text-wine">This project isn't available.</h1>
        <Link to="/portfolio" className="link-line mt-10">Back to Portfolio</Link>
      </div>
    );
  if (!project) return <div className="min-h-screen" data-testid="project-loading" />;

  const gallery = (project.images || []).filter((u) => u && u !== project.cover_image);
  const related = projects
    .filter((p) => p.slug !== project.slug)
    .sort((a, b) => (b.category === project.category) - (a.category === project.category))
    .slice(0, 3);
  const landscape = project.orientation === "landscape";

  return (
    <article data-testid="project-detail">
      <section className="mx-auto max-w-[1400px] px-6 pt-32 md:px-10 md:pt-40">
        <Link to="/portfolio" className="link-line" data-testid="project-back-link"><ArrowLeft size={14} strokeWidth={1.25} /> All Work</Link>
        <div className={`mt-10 grid gap-12 ${landscape ? "" : "md:grid-cols-12 md:gap-10"}`}>
          <div className={`frame overflow-hidden bg-sand fade-up ${landscape ? "aspect-[16/10] md:aspect-[21/10]" : "aspect-[4/5] md:col-span-7"}`}>
            <img src={mediaUrl(project.cover_image)} alt={`${project.title} — ${project.category} visual`} className="h-full w-full object-cover" data-testid="project-hero-image" />
          </div>
          <div className={landscape ? "grid gap-10 md:grid-cols-12" : "md:col-span-4 md:col-start-9 md:self-end"}>
            <div className={landscape ? "md:col-span-7" : ""}>
              <p className="eyebrow text-wine" data-testid="project-label">{project.label}</p>
              <h1 data-testid="project-title" className="mt-5 font-serif text-5xl font-light leading-[1.02] text-wine md:text-6xl">{project.title}</h1>
              <p className="mt-8 text-base leading-relaxed text-charcoal/85 md:text-lg" data-testid="project-description">{project.description}</p>
              {project.external_link && (
                <a href={project.external_link} target="_blank" rel="noopener noreferrer" className="link-line mt-8" data-testid="project-external-link">
                  View Externally <ArrowUpRight size={14} strokeWidth={1.25} />
                </a>
              )}
            </div>
            <dl className={`mt-10 ${landscape ? "md:col-span-4 md:col-start-9 md:mt-0" : ""}`}>
              <Meta label="Brand" value={project.brand} />
              <Meta label="Category" value={project.category} />
              <Meta label="Format" value={project.formats?.join(", ")} />
              <Meta label="Year" value={project.year} />
            </dl>
          </div>
        </div>
      </section>

      {project.video_url && <section className="mx-auto max-w-[1400px] px-6 pt-28 md:px-10 md:pt-40"><VideoBlock project={project} /></section>}

      {gallery.length > 0 && (
        <section className="mx-auto max-w-[1400px] px-6 pt-28 md:px-10 md:pt-40" data-testid="project-gallery">
          <p className="eyebrow mb-10 text-wine">Gallery</p>
          <div className={`grid gap-6 md:gap-10 ${gallery.length > 1 ? "md:grid-cols-2" : "mx-auto max-w-3xl"}`}>
            {gallery.map((src, i) => (
              <Reveal key={src} delay={(i % 2) * 0.1} className={i % 2 === 1 ? "md:mt-24" : ""}>
                <img src={mediaUrl(src)} alt={`${project.title} gallery image ${i + 1}`} loading="lazy" className="w-full" />
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {related.length > 0 && (
        <section className="mx-auto max-w-[1400px] px-6 py-28 md:px-10 md:py-40" data-testid="related-projects">
          <h2 className="font-serif text-4xl font-light text-wine md:text-5xl">More <span className="italic">Creations</span></h2>
          <div className="mt-14 grid gap-14 md:grid-cols-3 md:gap-8">
            {related.map((p, i) => (
              <Reveal key={p.slug} delay={i * 0.1}><ProjectCard project={p} aspect="aspect-[4/5]" index={5} /></Reveal>
            ))}
          </div>
        </section>
      )}
      <CollabCTA title="Imagine Your Brand Here." text="If this story resonates, let's create one for your product, collection or campaign." />
    </article>
  );
}
